// The project every screen of a project reads, and the one key it is cached
// under. Both live here rather than in any one page, because the pages that
// invalidate the key are not the pages that read it — and because a key
// written out by hand a thirteenth time is a thirteenth chance to disagree
// about what is stored under it.

import { useQuery, type QueryClient } from "@tanstack/react-query";

import { getProject, type PatchedProject, type ProjectDetail } from "./api";

/**
 * The cache entry for one project — `ProjectDetail` exactly as the server
 * answered, nothing wrapped around it.
 *
 * The shape is part of the contract, not an implementation detail of whoever
 * fetched first. Twelve screens used to write this key themselves and one of
 * them stored `{project, etag}` instead; with a 30-second `staleTime`
 * (lib/queryClient.ts) a screen that mounted onto somebody else's entry read
 * the other form and never asked the server — the player card lost the
 * profile schema, the leaderboard form claimed the project declared none, and
 * the breadcrumbs said "Project". The etag the wrapper carried is in the body
 * as well (`ProjectDetail.etag`); the `ETag` header only adds quotes, which
 * every caller strips anyway.
 *
 * Everything else a project owns hangs off this prefix — `activity`,
 * `analytics`, `api-keys`, `bots`, `transfer`, `wipe` — so invalidating this
 * key without `exact` takes all of them with it. That is what a rename, a
 * suspend, a finished wipe or a snapshot apply wants: each of them changes
 * more of the project than the record. What changes ONLY the record passes
 * `exact: true` — inviting, removing or losing a member moves
 * `members_count` / `pending_invites_count` and nothing else, and re-fetching
 * a project's analytics over it would be an answer nobody asked for.
 */
export function projectQueryKey(projectId: string) {
  return ["project", projectId] as const;
}

/**
 * The one way to read a project.
 *
 * Returns the whole `useQuery` result: callers need `isLoading` for their
 * render gate, `isError` / `refetch` for the "couldn't read this project"
 * alerts, and `data` for everything else. An empty id is the router's
 * "no project in this route yet" and asks nothing.
 */
export function useProject(projectId: string) {
  return useQuery<ProjectDetail>({
    queryKey: projectQueryKey(projectId),
    queryFn: () => getProject(projectId).then((r) => r.data),
    enabled: !!projectId,
  });
}

/**
 * The one way to WRITE a project — what a save puts back under the key.
 *
 * Reading was reduced to one hook while the two config editors kept a
 * byte-identical `setQueryData` each; that is the same trap from the other
 * end, since the next edit to one of the copies is what puts a second shape
 * under the key again — and this time with no duplicate key to make it
 * visible. Three rules the callers must not each remember on their own:
 *
 * - RAW. What goes in is the server's own answer, never `normalizeConfig(…)`:
 *   normalising belongs on the read, where all three config readers do it.
 * - OVER, not INSTEAD. A PATCH answers with `projectDTO` — no `created_by`,
 *   no `pending_invites_count` — and the overview reads both, so the answer
 *   is laid over the record already cached. Nothing is written when there is
 *   no such record: a partial project under this key is exactly what the key
 *   must never hold, and the next reader fetches a whole one.
 * - A FALLBACK for the config. A PATCH that echoes none would otherwise read
 *   as "this project declares none"; what was just saved is the truth about
 *   it.
 */
export function writeSavedProject(
  qc: QueryClient,
  projectId: string,
  saved: PatchedProject,
  savedConfig: unknown,
) {
  qc.setQueryData<ProjectDetail>(projectQueryKey(projectId), (prev) =>
    prev ? { ...prev, ...saved, config: saved.config ?? savedConfig } : prev,
  );
}
